import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit, Search, Filter, Printer, Building2,
  MapPin, Calendar, Bike, DollarSign, Upload, X, Save, Percent, Image
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtCurrency(v) {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v || 0);
}

// ======================= HOTELS TAB =======================
function HotelsTab() {
  const [hotels, setHotels] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', zone: '', commission_percentage: '' });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetch = useCallback(async () => {
    try { setHotels((await axios.get(`${API}/commissions/hotels`)).data); } catch {}
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const openNew = () => { setEditing(null); setForm({ name: '', zone: '', commission_percentage: '' }); setDialogOpen(true); };
  const openEdit = (h) => { setEditing(h); setForm({ name: h.name, zone: h.zone, commission_percentage: h.commission_percentage }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.name.trim() || !form.zone.trim()) { toast.error('Nombre y zona son obligatorios'); return; }
    const pct = parseFloat(form.commission_percentage);
    if (isNaN(pct) || pct < 0 || pct > 100) { toast.error('Porcentaje entre 0 y 100'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/commissions/hotels/${editing.id}`, { ...form, commission_percentage: pct });
        toast.success('Hotel actualizado');
      } else {
        await axios.post(`${API}/commissions/hotels`, { ...form, commission_percentage: pct });
        toast.success('Hotel creado');
      }
      setDialogOpen(false); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await axios.delete(`${API}/commissions/hotels/${deleteId}`); toast.success('Hotel eliminado'); setDeleteId(null); fetch(); }
    catch { toast.error('Error al eliminar'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{hotels.length} hoteles</p>
        <Button onClick={openNew} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-hotel-btn">
          <Plus size={16} className="mr-1" /> Nuevo Hotel
        </Button>
      </div>

      {hotels.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><Building2 size={40} className="mx-auto mb-3 text-slate-300" /><p className="text-slate-400">No hay hoteles</p></CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {hotels.map(h => (
            <Card key={h.id} className="hover:shadow-md transition-shadow" data-testid={`hotel-${h.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center shrink-0">
                  <Building2 size={20} className="text-indigo-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 truncate" data-testid="hotel-name">{h.name}</p>
                  <div className="flex items-center gap-3 mt-0.5 text-sm text-slate-500">
                    <span className="flex items-center gap-1"><MapPin size={12} /> {h.zone}</span>
                    <Badge className="bg-green-100 text-green-700 border-green-300"><Percent size={10} className="mr-0.5" /> {h.commission_percentage}%</Badge>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openEdit(h)} data-testid="edit-hotel-btn"><Edit size={14} /></Button>
                  <Button size="sm" variant="outline" className="text-red-500 hover:bg-red-50" onClick={() => setDeleteId(h.id)} data-testid="delete-hotel-btn"><Trash2 size={14} /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="hotel-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed']">{editing ? 'Editar Hotel' : 'Nuevo Hotel'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nombre del hotel" data-testid="hotel-name-input" /></div>
            <div><Label>Zona</Label><Input value={form.zone} onChange={e => setForm({ ...form, zone: e.target.value })} placeholder="Ej: Alcudia, Can Picafort..." data-testid="hotel-zone-input" /></div>
            <div><Label>% Comisión</Label><Input type="number" min="0" max="100" step="0.01" value={form.commission_percentage} onChange={e => setForm({ ...form, commission_percentage: e.target.value })} placeholder="Ej: 15" data-testid="hotel-pct-input" /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-hotel-btn">{saving ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Eliminar hotel</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-hotel">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ======================= LOGO TAB =======================
function LogoTab() {
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    axios.get(`${API}/commissions/shop-logo`).then(r => setLogoUrl(r.data.logo_url)).catch(() => {});
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/commissions/shop-logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLogoUrl(res.data.logo_url);
      toast.success('Logo actualizado');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al subir'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleDelete = async () => {
    try { await axios.delete(`${API}/commissions/shop-logo`); setLogoUrl(null); toast.success('Logo eliminado'); }
    catch { toast.error('Error'); }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">Logo de la tienda para documentos de impresión. Se mostrará como marca de agua.</p>
      {logoUrl ? (
        <div className="flex items-center gap-6">
          <div className="w-32 h-32 border rounded-lg flex items-center justify-center bg-white p-2">
            <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="shop-logo-preview" />
          </div>
          <div className="space-y-2">
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="change-logo-btn">
              <Upload size={14} className="mr-1" /> Cambiar
            </Button>
            <Button variant="outline" className="text-red-500 hover:bg-red-50 block" onClick={handleDelete} data-testid="delete-logo-btn">
              <Trash2 size={14} className="mr-1" /> Eliminar
            </Button>
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => fileRef.current?.click()} data-testid="upload-logo-area">
          <Image size={32} className="mx-auto mb-2 text-slate-300" />
          <p className="text-sm text-slate-500">{uploading ? 'Subiendo...' : 'Haz clic para subir el logo'}</p>
          <p className="text-xs text-slate-400 mt-1">PNG, JPG. Máx 2MB.</p>
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
    </div>
  );
}

// ======================= MAIN PAGE =======================
export default function Commissions() {
  const [commissions, setCommissions] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [zones, setZones] = useState([]);
  const [summary, setSummary] = useState({ total_client_price: 0, total_commission: 0, total_lines: 0, total_days: 0 });
  const [loading, setLoading] = useState(true);
  const [bikeModels, setBikeModels] = useState([]);

  // Filters
  const [filterHotel, setFilterHotel] = useState('all');
  const [filterZone, setFilterZone] = useState('all');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ hotel_id: '', days: '', bike_model: '', bike_quantity: 1, client_price: '', date: '' });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Print
  const [shopLogo, setShopLogo] = useState(null);

  const buildParams = useCallback(() => {
    const p = {};
    if (filterHotel !== 'all') p.hotel_id = filterHotel;
    if (filterZone !== 'all') p.zone = filterZone;
    if (filterFrom) p.date_from = filterFrom;
    if (filterTo) p.date_to = filterTo;
    return p;
  }, [filterHotel, filterZone, filterFrom, filterTo]);

  const fetchAll = useCallback(async () => {
    try {
      const params = buildParams();
      const [commRes, sumRes, hotelRes, zoneRes, logoRes] = await Promise.all([
        axios.get(`${API}/commissions`, { params }),
        axios.get(`${API}/commissions/summary`, { params }),
        axios.get(`${API}/commissions/hotels`),
        axios.get(`${API}/commissions/zones`),
        axios.get(`${API}/commissions/shop-logo`),
      ]);
      setCommissions(commRes.data);
      setSummary(sumRes.data);
      setHotels(hotelRes.data);
      setZones(zoneRes.data);
      setShopLogo(logoRes.data.logo_url);
    } catch {} finally { setLoading(false); }
  }, [buildParams]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Fetch bike models for selector
  useEffect(() => {
    axios.get(`${API}/bicycles`).then(r => {
      const models = [...new Set(r.data.map(b => b.model))].sort();
      setBikeModels(models);
    }).catch(() => {});
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ hotel_id: '', days: '', bike_model: '', bike_quantity: 1, client_price: '', date: new Date().toISOString().split('T')[0] });
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ hotel_id: c.hotel_id, days: c.days, bike_model: c.bike_model, bike_quantity: c.bike_quantity || 1, client_price: c.client_price, date: c.date || '' });
    setDialogOpen(true);
  };

  const selectedHotel = hotels.find(h => h.id === form.hotel_id);
  const previewCommission = selectedHotel && form.client_price
    ? round(parseFloat(form.client_price || 0) * selectedHotel.commission_percentage / 100)
    : null;

  const handleSave = async () => {
    if (!form.hotel_id) { toast.error('Selecciona un hotel'); return; }
    if (!form.days || parseInt(form.days) <= 0) { toast.error('Los días deben ser mayor que 0'); return; }
    if (!form.bike_model) { toast.error('Selecciona un modelo'); return; }
    if (form.client_price === '' || parseFloat(form.client_price) < 0) { toast.error('Precio inválido'); return; }
    setSaving(true);
    try {
      const payload = { ...form, days: parseInt(form.days), client_price: parseFloat(form.client_price), bike_quantity: parseInt(form.bike_quantity) || 1 };
      if (editing) {
        await axios.put(`${API}/commissions/${editing.id}`, payload);
        toast.success('Comisión actualizada');
      } else {
        await axios.post(`${API}/commissions`, payload);
        toast.success('Comisión creada');
      }
      setDialogOpen(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await axios.delete(`${API}/commissions/${deleteId}`); toast.success('Comisión eliminada'); setDeleteId(null); fetchAll(); }
    catch { toast.error('Error al eliminar'); }
  };

  const handlePrint = () => {
    const hotelName = filterHotel !== 'all' ? hotels.find(x => x.id === filterHotel)?.name : 'Todos los hoteles';
    const periodText = (filterFrom && filterTo) ? `${filterFrom}  —  ${filterTo}` 
      : filterFrom ? `Desde ${filterFrom}` 
      : filterTo ? `Hasta ${filterTo}` 
      : 'Todas las fechas';

    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Comisiones - ${hotelName}</title>
    <style>
      @page { size: A4; margin: 30mm 20mm; }
      body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 40px; color: #1e293b; }
      .watermark { position: fixed; top: 50%; left: 50%; transform: translate(-50%,-50%); opacity: 0.08; pointer-events: none; z-index: 0; width: 80%; max-width: 600px; }
      .content { position: relative; z-index: 1; }
      .header { margin-bottom: 32px; border-bottom: 3px solid #1e293b; padding-bottom: 16px; }
      .hotel-name { font-size: 28px; font-weight: 800; margin: 0; letter-spacing: -0.5px; }
      .period { font-size: 14px; color: #64748b; margin-top: 6px; }
      table { width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 20px; }
      th { padding: 10px 12px; text-align: left; border-bottom: 2px solid #cbd5e1; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #475569; }
      th:last-child { text-align: right; }
      td { padding: 9px 12px; border-bottom: 1px solid #e2e8f0; }
      td:last-child { text-align: right; }
      tr:nth-child(even) { background: #f8fafc; }
      .total-section { margin-top: 32px; border-top: 3px solid #1e293b; padding-top: 16px; display: flex; justify-content: flex-end; }
      .total-box { text-align: right; }
      .total-label { font-size: 13px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
      .total-value { font-size: 32px; font-weight: 800; color: #1e293b; margin-top: 4px; }
      @media print { body { padding: 0; } .no-print { display: none !important; } }
    </style></head><body>
    ${shopLogo ? `<img src="${shopLogo}" class="watermark" />` : ''}
    <div class="content">
      <div class="header">
        <h1 class="hotel-name">${hotelName}</h1>
        <p class="period">${periodText}</p>
      </div>
      <table>
        <thead><tr><th>Fecha</th><th>Cant.</th><th>Modelo</th><th>Comisión</th></tr></thead>
        <tbody>
          ${commissions.map(c => `<tr><td>${c.date}</td><td>${c.bike_quantity || 1}</td><td>${c.bike_model}</td><td>${fmtCurrency(c.commission_amount)}</td></tr>`).join('')}
        </tbody>
      </table>
      <div class="total-section">
        <div class="total-box">
          <div class="total-label">Total comisiones a pagar</div>
          <div class="total-value">${fmtCurrency(summary.total_commission)}</div>
        </div>
      </div>
    </div>
    <script>window.print();</script>
    </body></html>`);
    w.document.close();
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="commissions-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-800 font-['Barlow_Condensed']" data-testid="page-title">Comisiones</h1>
      </div>

      <Tabs defaultValue="commissions">
        <TabsList>
          <TabsTrigger value="commissions" data-testid="tab-commissions">Comisiones</TabsTrigger>
          <TabsTrigger value="hotels" data-testid="tab-hotels">Hoteles</TabsTrigger>
          <TabsTrigger value="logo" data-testid="tab-logo">Logo</TabsTrigger>
        </TabsList>

        {/* ====== COMMISSIONS TAB ====== */}
        <TabsContent value="commissions" className="space-y-4 mt-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
                <div>
                  <Label className="text-xs text-slate-500 flex items-center gap-1"><Building2 size={12} /> Hotel</Label>
                  <Select value={filterHotel} onValueChange={setFilterHotel}>
                    <SelectTrigger data-testid="filter-hotel"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {hotels.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 flex items-center gap-1"><MapPin size={12} /> Zona</Label>
                  <Select value={filterZone} onValueChange={setFilterZone}>
                    <SelectTrigger data-testid="filter-zone"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {zones.map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 flex items-center gap-1"><Calendar size={12} /> Desde</Label>
                  <Input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} data-testid="filter-from" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 flex items-center gap-1"><Calendar size={12} /> Hasta</Label>
                  <Input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} data-testid="filter-to" />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setFilterHotel('all'); setFilterZone('all'); setFilterFrom(''); setFilterTo(''); }} data-testid="clear-filters-btn">
                    <X size={14} className="mr-1" /> Limpiar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-slate-500 uppercase">Líneas</p><p className="text-2xl font-bold text-slate-800 font-['Barlow_Condensed']" data-testid="summary-lines">{summary.total_lines}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-slate-500 uppercase">Bicicletas</p><p className="text-2xl font-bold text-slate-800 font-['Barlow_Condensed']" data-testid="summary-bikes">{summary.total_bikes || 0}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-slate-500 uppercase">Días Totales</p><p className="text-2xl font-bold text-slate-800 font-['Barlow_Condensed']" data-testid="summary-days">{summary.total_days}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-xs text-slate-500 uppercase">Total Cliente</p><p className="text-2xl font-bold text-slate-800 font-['Barlow_Condensed']" data-testid="summary-client">{fmtCurrency(summary.total_client_price)}</p></CardContent></Card>
            <Card className="bg-emerald-50 border-emerald-200"><CardContent className="p-4 text-center"><p className="text-xs text-emerald-600 uppercase">Total Comisiones</p><p className="text-2xl font-bold text-emerald-700 font-['Barlow_Condensed']" data-testid="summary-commission">{fmtCurrency(summary.total_commission)}</p></CardContent></Card>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button onClick={openNew} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-commission-btn">
              <Plus size={16} className="mr-1" /> Nueva Comisión
            </Button>
            {commissions.length > 0 && (
              <Button variant="outline" onClick={handlePrint} data-testid="print-btn">
                <Printer size={16} className="mr-1" /> Imprimir
              </Button>
            )}
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
          ) : commissions.length === 0 ? (
            <Card><CardContent className="py-12 text-center"><DollarSign size={40} className="mx-auto mb-3 text-slate-300" /><p className="text-slate-400">No hay comisiones</p></CardContent></Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="commissions-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left p-3 font-medium text-slate-600">Fecha</th>
                    <th className="text-left p-3 font-medium text-slate-600">Hotel</th>
                    <th className="text-left p-3 font-medium text-slate-600">Zona</th>
                    <th className="text-right p-3 font-medium text-slate-600">Cant.</th>
                    <th className="text-left p-3 font-medium text-slate-600">Modelo</th>
                    <th className="text-right p-3 font-medium text-slate-600">Días</th>
                    <th className="text-right p-3 font-medium text-slate-600">Precio</th>
                    <th className="text-right p-3 font-medium text-slate-600">%</th>
                    <th className="text-right p-3 font-medium text-slate-600">Comisión</th>
                    <th className="text-right p-3 font-medium text-slate-600">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map(c => (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`commission-${c.id}`}>
                      <td className="p-3">{c.date}</td>
                      <td className="p-3 font-medium">{c.hotel_name}</td>
                      <td className="p-3"><Badge variant="outline" className="text-xs">{c.zone}</Badge></td>
                      <td className="p-3 text-right">{c.bike_quantity || 1}</td>
                      <td className="p-3">{c.bike_model}</td>
                      <td className="p-3 text-right">{c.days}</td>
                      <td className="p-3 text-right">{fmtCurrency(c.client_price)}</td>
                      <td className="p-3 text-right text-slate-500">{c.commission_percentage}%</td>
                      <td className="p-3 text-right font-semibold text-emerald-700">{fmtCurrency(c.commission_amount)}</td>
                      <td className="p-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid="edit-commission-btn"><Edit size={14} /></Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => setDeleteId(c.id)} data-testid="delete-commission-btn"><Trash2 size={14} /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ====== HOTELS TAB ====== */}
        <TabsContent value="hotels" className="mt-4"><HotelsTab /></TabsContent>

        {/* ====== LOGO TAB ====== */}
        <TabsContent value="logo" className="mt-4"><LogoTab /></TabsContent>
      </Tabs>

      {/* Commission Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="commission-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed']">{editing ? 'Editar Comisión' : 'Nueva Comisión'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Fecha</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="commission-date-input" />
            </div>
            <div>
              <Label>Hotel</Label>
              <Select value={form.hotel_id} onValueChange={v => setForm({ ...form, hotel_id: v })}>
                <SelectTrigger data-testid="commission-hotel-select"><SelectValue placeholder="Seleccionar hotel..." /></SelectTrigger>
                <SelectContent>
                  {hotels.map(h => <SelectItem key={h.id} value={h.id}>{h.name} ({h.zone}) - {h.commission_percentage}%</SelectItem>)}
                </SelectContent>
              </Select>
              {selectedHotel && (
                <p className="text-xs text-slate-400 mt-1">Zona: {selectedHotel.zone} | Comisión: {selectedHotel.commission_percentage}%</p>
              )}
            </div>
            <div>
              <Label>Modelo de Bicicleta</Label>
              <Select value={form.bike_model} onValueChange={v => setForm({ ...form, bike_model: v })}>
                <SelectTrigger data-testid="commission-model-select"><SelectValue placeholder="Seleccionar modelo..." /></SelectTrigger>
                <SelectContent>
                  {bikeModels.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Cant. Bicis</Label>
                <Input type="number" min="1" value={form.bike_quantity} onChange={e => setForm({ ...form, bike_quantity: e.target.value })} placeholder="1" data-testid="commission-quantity-input" />
              </div>
              <div>
                <Label>Días</Label>
                <Input type="number" min="1" value={form.days} onChange={e => setForm({ ...form, days: e.target.value })} placeholder="Ej: 7" data-testid="commission-days-input" />
              </div>
              <div>
                <Label>Precio Cliente</Label>
                <Input type="number" min="0" step="0.01" value={form.client_price} onChange={e => setForm({ ...form, client_price: e.target.value })} placeholder="Ej: 150.00" data-testid="commission-price-input" />
              </div>
            </div>
            {previewCommission !== null && (
              <div className="bg-emerald-50 rounded-lg p-3 flex items-center justify-between">
                <span className="text-sm text-emerald-700">Comisión ({selectedHotel.commission_percentage}%)</span>
                <span className="text-lg font-bold text-emerald-800 font-['Barlow_Condensed']" data-testid="commission-preview">{fmtCurrency(previewCommission)}</span>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-commission-btn">
              {saving ? 'Guardando...' : <><Save size={14} className="mr-1" /> Guardar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Eliminar comisión</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-commission">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function round(v) { return Math.round(v * 100) / 100; }
